import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { hapticImpact } from "@/lib/haptics";
import { useSPAContext } from "@/providers/spa-provider";
import { RouteNames } from "@/types/route-names";

type CommandRowProps = {
  title: string;
  tag: string;
  subtitle?: string;
  icon?: string;
  accent?: boolean;
  active?: boolean;
  disabled?: boolean;
  delayMs?: number;
  onClick: () => void;
};

const CommandRow = ({
  title,
  tag,
  subtitle,
  icon,
  accent,
  active,
  disabled,
  delayMs = 0,
  onClick,
}: CommandRowProps) => (
  <button
    type="button"
    disabled={disabled}
    onClick={() => {
      if (disabled) return;
      hapticImpact();
      onClick();
    }}
    className={[
      "group relative flex w-full items-center gap-2 px-2.5 text-left transition-all",
      "border-b-2 border-pk-blue-5/25 last:border-b-0",
      "disabled:opacity-60 disabled:cursor-not-allowed",
      "active:bg-pk-blue-3/50",
      accent ? "py-3 bg-pk-garnet-2/35" : "py-2.5",
      active ? "animate-pulse bg-pk-red-2/40" : "",
      "animate-slide-left-fade opacity-0",
    ].join(" ")}
    style={{ animationDelay: `${delayMs}ms` }}
  >
    <span
      className={[
        "w-3 shrink-0 font-black text-pk-yellow-3 text-sm leading-none",
        "opacity-0 group-active:opacity-100 group-hover:opacity-100",
        accent || active ? "opacity-100" : "",
      ].join(" ")}
      aria-hidden
    >
      ▶
    </span>

    <div className="flex-1 min-w-0 flex flex-col gap-0.5">
      <div className="flex items-center gap-2">
        <span
          className={[
            "font-black uppercase tracking-wider drop-shadow-[1px_1px_0_rgba(0,0,0,0.7)]",
            accent ? "text-white text-base" : "text-white text-sm",
          ].join(" ")}
        >
          {title}
        </span>
        <span
          className={[
            "border border-black px-1.5 py-px font-black text-[8px] uppercase tracking-wider shadow-[0_1px_0_rgba(0,0,0,1)]",
            accent
              ? "bg-pk-garnet-1 text-pk-cream-2"
              : "bg-pk-night-1 text-pk-gray-4",
          ].join(" ")}
        >
          {tag}
        </span>
      </div>
      {subtitle && (
        <span className="text-pk-gray-4 text-[10px] font-bold uppercase tracking-wide truncate">
          {subtitle}
        </span>
      )}
    </div>

    {icon && (
      <img
        src={icon}
        alt=""
        className="w-9 h-9 object-contain shrink-0 drop-shadow-md"
      />
    )}
  </button>
);

type PlayMenuCommandListProps = {
  isDuelActive: boolean;
  isDuelWaiting: boolean;
  isInitializingDuel: boolean;
  onArena: () => void;
  onReconnect: () => void;
};

export const PlayMenuCommandList = ({
  isDuelActive,
  isDuelWaiting,
  isInitializingDuel,
  onArena,
  onReconnect,
}: PlayMenuCommandListProps) => {
  const { setCurrentRoute } = useSPAContext();

  const arenaTitle = isDuelWaiting
    ? "In Queue..."
    : isDuelActive
      ? "Reconnect"
      : "Arena";
  const arenaSubtitle = isDuelWaiting
    ? "Waiting for match..."
    : isDuelActive
      ? "Battle in progress"
      : "Ranked battles";

  return (
    <StandardFrame variant="blue" rounded className="overflow-hidden">
      <div className="px-3 pt-2 pb-1 border-b-2 border-pk-blue-5/40">
        <span className="text-pk-blue-5 text-[9px] font-black uppercase tracking-[0.2em]">
          Select Mode
        </span>
      </div>

      <CommandRow
        title={arenaTitle}
        tag="PvP"
        subtitle={arenaSubtitle}
        icon="/assets/icons/medal.png"
        accent
        active={isDuelActive || isDuelWaiting}
        disabled={isInitializingDuel || isDuelWaiting}
        delayMs={0}
        onClick={isDuelActive ? onReconnect : onArena}
      />
      <CommandRow
        title="Campaign"
        tag="Co-op"
        subtitle="World map conquest"
        delayMs={60}
        onClick={() => setCurrentRoute(RouteNames.SIEGE)}
      />
      <CommandRow
        title="Challenge"
        tag="Boss"
        subtitle="Raid bosses"
        delayMs={120}
        onClick={() => setCurrentRoute(RouteNames.CHALLENGE)}
      />
      <CommandRow
        title="Auction"
        tag="Econ"
        subtitle="Trade & bid"
        delayMs={180}
        onClick={() => setCurrentRoute(RouteNames.AUCTION)}
      />
      <CommandRow
        title="Dojo"
        tag="Solo"
        subtitle="Practice sandbox"
        delayMs={240}
        onClick={() => setCurrentRoute(RouteNames.DOJO)}
      />
    </StandardFrame>
  );
};
